// src/features/files/services/files.js
import { apiClient } from '../../../services/api/client';

export const filesAPI = {
  // Upload a file as the raw request body. The server writes it to storage as
  // it arrives (no multipart, no buffering), so size is only limited by the
  // server's MAX_UPLOAD_FILE_SIZE. No timeout: big files on slow links take a while.
  uploadFile: (file, queryParams = '', { onUploadProgress } = {}) => {
    const params = new URLSearchParams(queryParams);
    params.set('filename', file.name || 'unnamed');
    return apiClient.post(`/api/v1/file/files/upload/stream?${params.toString()}`, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      timeout: 0,
      onUploadProgress,
    });
  },

  // Download file
  downloadFile: (fileId, previewSize = 'original', versionNumber = null) => {
    const params = new URLSearchParams();
    if (previewSize !== 'original') params.append('size', previewSize);
    if (versionNumber) params.append('version', versionNumber);
    const qs = params.toString();
    return apiClient.get(`/api/v1/file/files/download/${fileId}${qs ? '?' + qs : ''}`, { responseType: 'blob' });
  },

  // Signed, short-lived link that opens the file without an Authorization header
  // (for <video>/<embed>/<img> src and download links). See useFileUrl.
  getAccessUrl: (fileId, { version = null } = {}) => {
    const params = new URLSearchParams();
    if (version) params.append('version', version);
    const qs = params.toString();
    return apiClient.post(`/api/v1/file/files/${fileId}/access-url${qs ? '?' + qs : ''}`);
  },

  // Get file details
  getFileDetails: (fileId) => 
    apiClient.get(`/api/v1/file/files/${fileId}`),

  // List my files
  listMyFiles: (limit = null, offset = null) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    
    const url = params.toString()
      ? `/api/v1/file/files/my/list?${params.toString()}`
      : '/api/v1/file/files/my/list';
    
    return apiClient.get(url);
  },

  // List department files
  listDepartmentFiles: (limit = null, offset = null) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    
    const url = params.toString()
      ? `/api/v1/file/files/department/list?${params.toString()}`
      : '/api/v1/file/files/department/list';
    
    return apiClient.get(url);
  },

  // Get department storage usage
  getDepartmentUsage: () => 
    apiClient.get('/api/v1/file/files/department/usage'),

  // Update file metadata
  moveFile: (fileId, newFolderId) =>
    apiClient.post(`/api/v1/file/files/${fileId}/move`, { new_folder_id: newFolderId ?? null }),

  // Who may see and change the file (needs file:permissions)
  getAccess: (fileId) =>
    apiClient.get(`/api/v1/file/files/${fileId}/access`),

  setAccess: (fileId, { inherit, rules }) =>
    apiClient.put(`/api/v1/file/files/${fileId}/access`, { inherit, rules }),

  updateFileMetadata: (fileId, data) =>
    apiClient.patch(`/api/v1/file/files/${fileId}`, data),

  // Where a file is used (equipment, tasks, safety data sheets, ...); a file in use can't be deleted
  getFileUsage: (fileId) =>
    apiClient.get(`/api/v1/file/files/${fileId}/usage`),

  // Delete file
  deleteFile: (fileId, forcePhysicalDelete = false) => {
    const url = forcePhysicalDelete
      ? `/api/v1/file/files/${fileId}?force_physical_delete=true`
      : `/api/v1/file/files/${fileId}`;
    return apiClient.delete(url);
  },

  // Set profile picture
  setProfilePicture: (staffMemberId, fileId) => 
    apiClient.put(`/api/v1/ac/staff-members/${staffMemberId}/profile-picture?file_id=${fileId}`),

  // Remove profile picture
  removeProfilePicture: (staffMemberId) =>
    apiClient.delete(`/api/v1/ac/staff-members/${staffMemberId}/profile-picture`),

  // Mark file as read — versioned endpoint if versionNumber provided
  markFileRead: (fileId, versionNumber = null) =>
    versionNumber
      ? apiClient.post(`/api/v1/file/files/${fileId}/versions/${versionNumber}/mark-read`)
      : apiClient.post(`/api/v1/file/files/${fileId}/mark-read`),

  // List all versions of a file
  listFileVersions: (fileId) =>
    apiClient.get(`/api/v1/file/files/${fileId}/versions`),

  // Upload a new version of an existing file
  uploadFileVersion: (fileId, file, changeNote = null, { onUploadProgress } = {}) => {
    const qs = changeNote ? `?change_note=${encodeURIComponent(changeNote)}` : '';
    return apiClient.post(`/api/v1/file/files/${fileId}/versions/stream${qs}`, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      timeout: 0,
      onUploadProgress,
    });
  },

  // Get readers for a specific version
  getVersionReaders: (fileId, versionNumber, limit = null, offset = null) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    const qs = params.toString();
    return apiClient.get(`/api/v1/file/files/${fileId}/versions/${versionNumber}/readers${qs ? '?' + qs : ''}`);
  },

  // Get file readers (who has accessed the file)
  getFileReaders: (fileId, limit = null, offset = null) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    const url = params.toString()
      ? `/api/v1/file/files/${fileId}/readers?${params.toString()}`
      : `/api/v1/file/files/${fileId}/readers`;
    return apiClient.get(url);
  },

  // Get my read history
  getMyReadHistory: (limit = null, offset = null) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    const url = params.toString()
      ? `/api/v1/file/files/activity/my?${params.toString()}`
      : '/api/v1/file/files/activity/my';
    return apiClient.get(url);
  },
};